/**
 * Article Form Page
 *
 * Mobile-first form for creating and editing articles.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ArrowLeft, Plus, Minus, Package, Printer, ScanBarcode } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import api from '@/lib/api'
import { t } from '@/i18n'
import { useCreateArticle, useUpdateArticle, useUpdateStock, useFamilies, useGroups, useProviders, useUbications, useNextArticleCode } from '@/hooks/useArticles'
import { parseApiError } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { isAxiosError } from 'axios'
import { CameraScannerDialog } from '@/components/billing/CameraScannerDialog'
import { playScanReadSound, primeScanAudio } from '@/lib/barcodeScanner'
import { shouldOfferCameraScan } from '@/lib/cameraScanner'

const TAX_RATE = 0.13

// El lector termina cada código con Enter: en un campo de código pasa al
// siguiente en vez de guardar el artículo a medio llenar.
function preventScannerSubmit(event: React.KeyboardEvent<HTMLInputElement>) {
  if (event.key !== 'Enter') return
  event.preventDefault()
  const fields = Array.from(
    event.currentTarget.form?.querySelectorAll<HTMLElement>('input:not([disabled]), textarea, button[role="combobox"]') ?? []
  )
  fields[fields.indexOf(event.currentTarget) + 1]?.focus()
}

const articleSchema = z.object({
  code: z.string().optional(),
  alternative_code: z.string().max(50).optional(),
  description: z.string().min(1, t('validation.required')),
  purchase_cost: z.coerce.number().min(0, t('validation.positive')),
  profit_1: z.coerce.number().min(0).max(999),
  profit_2: z.coerce.number().min(0).max(999),
  profit_3: z.coerce.number().min(0).max(999),
  sale_price_1: z.coerce.number().min(0),
  sale_price_2: z.coerce.number().min(0),
  sale_price_3: z.coerce.number().min(0),
  sale_price_1_iva: z.coerce.number().min(0),
  sale_price_2_iva: z.coerce.number().min(0),
  sale_price_3_iva: z.coerce.number().min(0),
  applied_sale_tax: z.boolean(),
  count_articles: z.coerce.number().int(),
  minimum_stock: z.coerce.number().int(),
  maximum_stock: z.coerce.number().int(),
  cabys_code: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{13}$/.test(v), {
      message: t('articles.cabysMust13'),
    }),
  family_id: z.coerce.number().optional().nullable(),
  group_id: z.coerce.number().optional().nullable(),
  provider_id: z.coerce.number().optional().nullable(),
  ubication_id: z.coerce.number().optional().nullable(),
})

type ArticleFormValues = z.infer<typeof articleSchema>

interface SharedAlternativeCode {
  code: string
  articles: { id: number; code: string; description: string }[]
  values: ArticleFormValues
}

function sharedAlternativeCodeConflict(error: unknown) {
  if (!isAxiosError(error) || error.response?.status !== 409) return null
  const detail = error.response.data?.detail
  return detail?.error === 'alternative_code_shared'
    ? (detail.articles as SharedAlternativeCode['articles'])
    : null
}

export function ArticleFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: article, isLoading: isLoadingArticle } = useQuery({
    queryKey: ['articles', Number(id)],
    queryFn: async () => {
      const response = await api.get(`/articles/${id}`)
      return response.data
    },
    enabled: isEditing,
  })

  const { data: nextCode } = useNextArticleCode(!isEditing)
  const createArticle = useCreateArticle()
  const updateArticle = useUpdateArticle()
  const updateStock = useUpdateStock()

  // Stock adjustment state
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease'>('increase')
  const [adjustmentQty, setAdjustmentQty] = useState<number>(0)
  const [adjustmentReason, setAdjustmentReason] = useState('')
  const [stockMessage, setStockMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleStockAdjustment = async () => {
    if (!id || adjustmentQty <= 0) {
      setStockMessage({ type: 'error', text: t('articles.adjustmentRequired') })
      return
    }
    try {
      const quantity = adjustmentType === 'increase' ? adjustmentQty : -adjustmentQty
      const result = await updateStock.mutateAsync({ id: Number(id), quantity, reason: adjustmentReason.trim() || undefined })
      form.setValue('count_articles', result.count_articles)
      setAdjustmentQty(0)
      setAdjustmentReason('')
      setStockMessage({ type: 'success', text: t('articles.stockAdjustedSuccess') })
      setTimeout(() => setStockMessage(null), 3000)
    } catch (error) {
      setStockMessage({ type: 'error', text: parseApiError(error) })
    }
  }

  const { data: families = [] } = useFamilies()
  const { data: groups = [] } = useGroups()
  const { data: providers = [] } = useProviders()
  const { data: ubications = [] } = useUbications()

  const form = useForm<ArticleFormValues>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      code: '',
      alternative_code: '',
      description: '',
      purchase_cost: 0,
      profit_1: 30,
      profit_2: 25,
      profit_3: 20,
      sale_price_1: 0,
      sale_price_2: 0,
      sale_price_3: 0,
      sale_price_1_iva: 0,
      sale_price_2_iva: 0,
      sale_price_3_iva: 0,
      applied_sale_tax: true,
      count_articles: 0,
      minimum_stock: 0,
      maximum_stock: 0,
      cabys_code: '',
      family_id: null,
      group_id: null,
      provider_id: null,
      ubication_id: null,
    },
  })

  // Skip price recalculation while loading saved article data, so stored
  // final prices are preserved exactly (no float-rounding drift). Cleared
  // asynchronously so it survives every render triggered by form.reset,
  // including the initial-mount case where the article is already cached.
  const skipNextRecalcRef = React.useRef(false)

  // Remember which article id has already been hydrated into the form, so a
  // refetch of the same article does not re-reset (and clobber unsaved edits).
  const loadedArticleIdRef = React.useRef<number | null>(null)

  // Load article data when editing.
  // Only hydrate the first time the article loads (or when navigating to a
  // different id). A refetch of the same article — e.g. the one triggered when
  // a stock adjustment invalidates the 'articles' cache — must NOT reset the
  // form, or it would discard unsaved edits (prices, profit margins, etc.).
  useEffect(() => {
    if (article && article.id !== loadedArticleIdRef.current) {
      loadedArticleIdRef.current = article.id
      skipNextRecalcRef.current = true
      form.reset({
        code: article.code,
        alternative_code: article.alternative_code || '',
        description: article.description,
        purchase_cost: article.purchase_cost,
        profit_1: article.profit_1,
        profit_2: article.profit_2,
        profit_3: article.profit_3,
        sale_price_1: article.sale_price_1,
        sale_price_2: article.sale_price_2,
        sale_price_3: article.sale_price_3,
        sale_price_1_iva: article.sale_price_1_iva,
        sale_price_2_iva: article.sale_price_2_iva,
        sale_price_3_iva: article.sale_price_3_iva,
        applied_sale_tax: article.applied_sale_tax,
        count_articles: article.count_articles,
        minimum_stock: article.minimum_stock,
        maximum_stock: article.maximum_stock ?? 0,
        cabys_code: article.cabys_code || '',
        family_id: article.family_id || null,
        group_id: article.group_id || null,
        provider_id: article.provider_id || null,
        ubication_id: article.ubication_id || null,
      })
      const t = setTimeout(() => { skipNextRecalcRef.current = false }, 0)
      return () => clearTimeout(t)
    }
  }, [article, form])

  // Pre-populate code with next auto-generated code for new articles
  useEffect(() => {
    if (!isEditing && nextCode && !form.getValues('code')) {
      form.setValue('code', nextCode)
    }
  }, [nextCode, isEditing, form])

  const { data: parameters } = useQuery({
    queryKey: ['parameters'],
    queryFn: async () => {
      const response = await api.get('/parameters')
      return response.data as { code: string; value: number | null }[]
    },
    enabled: !isEditing,
  })

  // Márgenes por defecto de la empresa (Configuración) para artículos nuevos.
  useEffect(() => {
    if (isEditing || !parameters) return
    for (const level of [1, 2, 3] as const) {
      const field = `profit_${level}` as const
      const param = parameters.find(p => p.code === `default_profit_${level}`)
      const untouched = form.getValues(field) === form.formState.defaultValues?.[field]
      if (param?.value != null && untouched) {
        form.setValue(field, param.value)
      }
    }
  }, [parameters, isEditing, form])

  // Track whether the user is editing a final price (to avoid calculation loops)
  const editingSource = React.useRef<'profit' | 'price' | null>(null)

  // Calculate prices when cost or profits change
  // Aviso, no bloqueo: el código alterno puede repetirse, pero entonces el
  // lector pregunta cuál agregar.
  const watchedAlternativeCode = form.watch('alternative_code')?.trim() ?? ''
  const [cameraAvailable] = useState(shouldOfferCameraScan)
  const [showCamera, setShowCamera] = useState(false)
  const handleCameraCode = (code: string) => {
    playScanReadSound()
    form.setValue('alternative_code', code, { shouldDirty: true, shouldValidate: true })
    return true
  }
  const [alternativeCodeToCheck, setAlternativeCodeToCheck] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setAlternativeCodeToCheck(watchedAlternativeCode), 400)
    return () => clearTimeout(timer)
  }, [watchedAlternativeCode])
  const { data: sharedAlternativeCode = [] } = useQuery({
    queryKey: ['article-lookup', alternativeCodeToCheck],
    queryFn: async () => {
      try {
        const response = await api.get<{ items: { id: number; code: string; description: string }[] }>(
          '/articles/lookup',
          { params: { code: alternativeCodeToCheck } }
        )
        return response.data.items
      } catch {
        return []
      }
    },
    enabled: alternativeCodeToCheck.length >= 3,
    select: (items) => items.filter((item) => item.id !== Number(id)),
  })

  const watchedCost = form.watch('purchase_cost')
  const watchedProfit1 = form.watch('profit_1')
  const watchedProfit2 = form.watch('profit_2')
  const watchedProfit3 = form.watch('profit_3')
  const watchedApplyTax = form.watch('applied_sale_tax')

  useEffect(() => {
    if (skipNextRecalcRef.current) return
    if (editingSource.current === 'price') return

    const cost = Number(watchedCost) || 0
    const profit1 = Number(watchedProfit1) || 0
    const profit2 = Number(watchedProfit2) || 0
    const profit3 = Number(watchedProfit3) || 0

    const price1 = cost * (1 + profit1 / 100)
    const price2 = cost * (1 + profit2 / 100)
    const price3 = cost * (1 + profit3 / 100)

    const taxMultiplier = watchedApplyTax ? 1 + TAX_RATE : 1

    form.setValue('sale_price_1', Math.round(price1 * 100) / 100, { shouldDirty: true })
    form.setValue('sale_price_2', Math.round(price2 * 100) / 100, { shouldDirty: true })
    form.setValue('sale_price_3', Math.round(price3 * 100) / 100, { shouldDirty: true })
    form.setValue('sale_price_1_iva', Math.round(price1 * taxMultiplier * 100) / 100, { shouldDirty: true })
    form.setValue('sale_price_2_iva', Math.round(price2 * taxMultiplier * 100) / 100, { shouldDirty: true })
    form.setValue('sale_price_3_iva', Math.round(price3 * taxMultiplier * 100) / 100, { shouldDirty: true })
  }, [watchedCost, watchedProfit1, watchedProfit2, watchedProfit3, watchedApplyTax, form])

  // Reverse calculation: final price → profit percentage
  const handlePriceChange = (priceLevel: 1 | 2 | 3, finalPrice: number) => {
    editingSource.current = 'price'
    const cost = Number(form.getValues('purchase_cost')) || 0
    const applyTax = form.getValues('applied_sale_tax')

    const priceWithoutTax = applyTax ? finalPrice / (1 + TAX_RATE) : finalPrice
    const profitPercent = cost > 0 ? Math.round(((priceWithoutTax - cost) / cost) * 10000) / 100 : 0

    const profitField = `profit_${priceLevel}` as const
    const salePriceField = `sale_price_${priceLevel}` as const
    const salePriceIvaField = `sale_price_${priceLevel}_iva` as const

    form.setValue(profitField, Math.max(0, profitPercent), { shouldDirty: true })
    form.setValue(salePriceField, Math.round(priceWithoutTax * 100) / 100, { shouldDirty: true })
    form.setValue(salePriceIvaField, Math.round(finalPrice * 100) / 100, { shouldDirty: true })

    // Reset the flag after React processes the state updates
    setTimeout(() => { editingSource.current = null }, 0)
  }

  const [sharedCodeConfirm, setSharedCodeConfirm] = useState<SharedAlternativeCode | null>(null)
  // "Guardar e imprimir": sobrevive a la confirmación del código alterno compartido.
  const printAfterSave = useRef(false)

  const saveArticle = async (data: ArticleFormValues, confirmSharedAlternativeCode = false) => {
    try {
      const articleData = {
        ...data,
        confirm_shared_alternative_code: confirmSharedAlternativeCode,
        code: data.code || undefined,
        // null y no undefined: undefined no viaja y el backend dejaría el anterior.
        alternative_code: data.alternative_code?.trim() || null,
        family_id: data.family_id || undefined,
        group_id: data.group_id || undefined,
        provider_id: data.provider_id || undefined,
        ubication_id: data.ubication_id || undefined,
        cabys_code: data.cabys_code || undefined,
      }

      let saved
      if (isEditing) {
        // Las existencias no viajan: son las de cuando se abrió el formulario y
        // pisarían lo que otro equipo vendió mientras tanto. Se ajustan aparte.
        const { count_articles: _stock, ...editData } = articleData
        saved = await updateArticle.mutateAsync({ id: Number(id), data: editData })
      } else {
        saved = await createArticle.mutateAsync(articleData)
      }
      navigate(printAfterSave.current ? `/articles/labels?ids=${saved.id}` : '/articles')
    } catch (error) {
      const articles = sharedAlternativeCodeConflict(error)
      if (articles) {
        setSharedCodeConfirm({ code: data.alternative_code?.trim() ?? '', articles, values: data })
        return
      }
      form.setError('root', { message: parseApiError(error) })
    }
  }

  const onSubmit = (data: ArticleFormValues) => saveArticle(data)

  const isLoading = createArticle.isPending || updateArticle.isPending

  if (isEditing && isLoadingArticle) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-bold">
          {isEditing ? t('articles.editArticle') : t('articles.newArticle')}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {form.formState.errors.root && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {form.formState.errors.root.message}
            </div>
          )}

          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('articles.basicInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.code')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isEditing}
                          onKeyDown={preventScannerSubmit}
                          className="h-12 text-base"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {isEditing ? '' : t('articles.codeEditableHint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="alternative_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.alternativeCode')}</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input {...field} onKeyDown={preventScannerSubmit} className="h-12 text-base" />
                        </FormControl>
                        {cameraAvailable && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-12 w-12 shrink-0"
                            onClick={() => {
                              primeScanAudio()
                              setShowCamera(true)
                            }}
                            aria-label={t('pos.cameraScan')}
                            title={t('pos.cameraScan')}
                          >
                            <ScanBarcode className="h-5 w-5" />
                          </Button>
                        )}
                      </div>
                      <FormDescription className="text-xs">
                        {t('articles.alternativeCodeHint')}
                      </FormDescription>
                      {sharedAlternativeCode.map((item) => (
                        <p key={item.id} className="text-xs text-yellow-600 dark:text-yellow-500">
                          {t('articles.alternativeCodeShared', { code: item.code, description: item.description })}
                        </p>
                      ))}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cabys_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.cabysCode')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="numeric"
                          maxLength={13}
                          onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 13))}
                          placeholder={t('articles.cabysPlaceholder')}
                          className="h-12 text-base"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">{t('articles.cabysCodeHelp')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('articles.description')} *</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-12 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Categories */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('articles.categories')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="family_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.family')}</FormLabel>
                      <SearchableSelect
                        options={families.map((f: { id: number; name: string }) => ({ value: f.id.toString(), label: f.name }))}
                        value={field.value?.toString() || ''}
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        placeholder={t('common.select')}
                        searchPlaceholder={t('common.search')}
                        className="h-12"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.group')}</FormLabel>
                      <SearchableSelect
                        options={groups.map((g: { id: number; name: string }) => ({ value: g.id.toString(), label: g.name }))}
                        value={field.value?.toString() || ''}
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        placeholder={t('common.select')}
                        searchPlaceholder={t('common.search')}
                        className="h-12"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="provider_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.provider')}</FormLabel>
                      <SearchableSelect
                        options={providers.map((p: { id: number; name: string }) => ({ value: p.id.toString(), label: p.name }))}
                        value={field.value?.toString() || ''}
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        placeholder={t('common.select')}
                        searchPlaceholder={t('common.search')}
                        className="h-12"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ubication_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.location')}</FormLabel>
                      <SearchableSelect
                        options={ubications.map((u: { id: number; name: string }) => ({ value: u.id.toString(), label: u.name }))}
                        value={field.value?.toString() || ''}
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        placeholder={t('common.select')}
                        searchPlaceholder={t('common.search')}
                        className="h-12"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('articles.pricing')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="purchase_cost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.purchaseCost')} *</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} className="h-12 text-base" inputMode="decimal" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profit_1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.profitPrice1')}</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} className="h-12 text-base" inputMode="decimal" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profit_2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.profitPrice2')}</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} className="h-12 text-base" inputMode="decimal" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="profit_3"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.profitPrice3')}</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} className="h-12 text-base" inputMode="decimal" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="applied_sale_tax"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-6 w-6"
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 text-base">{t('articles.applyIva')}</FormLabel>
                  </FormItem>
                )}
              />

              {/* Editable Final Prices */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FormField
                  control={form.control}
                  name="sale_price_1_iva"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm text-primary">{t('articles.price1')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          {...field}
                          className="h-12 text-base font-bold"
                          inputMode="decimal"
                          onChange={(e) => {
                            field.onChange(e)
                            const val = e.target.value
                            if (val !== '' && val !== '-') {
                              handlePriceChange(1, Number(val))
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sale_price_2_iva"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.price2')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          {...field}
                          className="h-12 text-base font-bold"
                          inputMode="decimal"
                          onChange={(e) => {
                            field.onChange(e)
                            const val = e.target.value
                            if (val !== '' && val !== '-') {
                              handlePriceChange(2, Number(val))
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sale_price_3_iva"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('articles.price3')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          {...field}
                          className="h-12 text-base font-bold"
                          inputMode="decimal"
                          onChange={(e) => {
                            field.onChange(e)
                            const val = e.target.value
                            if (val !== '' && val !== '-') {
                              handlePriceChange(3, Number(val))
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stock */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                {t('articles.inventory')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="count_articles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.currentStock')}</FormLabel>
                      {isEditing ? (
                        <div className="flex items-center gap-2 h-12">
                          <span className="text-2xl font-bold">{field.value}</span>
                          <span className="text-sm text-muted-foreground">{t('articles.units')}</span>
                          {article && article.minimum_stock > 0 && field.value <= article.minimum_stock && (
                            <Badge variant="destructive" className="text-xs">{t('articles.lowStock')}</Badge>
                          )}
                        </div>
                      ) : (
                        <>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              className="h-12 text-base"
                              inputMode="numeric"
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            {t('articles.initialQuantity')}
                          </FormDescription>
                        </>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="minimum_stock"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.minimumStock')}</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} className="h-12 text-base" inputMode="numeric" />
                      </FormControl>
                      <FormDescription className="text-xs">{t('articles.alertWhenLow')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maximum_stock"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('articles.maximumStock')}</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} className="h-12 text-base" inputMode="numeric" />
                      </FormControl>
                      <FormDescription className="text-xs">{t('articles.alertWhenHigh')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Stock Adjustment - only when editing */}
              {isEditing && (
                <div className="border rounded-lg p-3 sm:p-4 space-y-3 bg-muted/30">
                  <h4 className="font-medium text-sm">{t('articles.stockAdjustment')}</h4>

                  {stockMessage && (
                    <div className={`text-sm p-2 rounded ${stockMessage.type === 'success' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-destructive/10 text-destructive'}`}>
                      {stockMessage.text}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t('articles.adjustmentType')}</label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={adjustmentType === 'increase' ? 'default' : 'outline'}
                          className="flex-1 h-10"
                          onClick={() => setAdjustmentType('increase')}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('articles.increase')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={adjustmentType === 'decrease' ? 'destructive' : 'outline'}
                          className="flex-1 h-10"
                          onClick={() => setAdjustmentType('decrease')}
                        >
                          <Minus className="h-4 w-4 mr-1" />
                          {t('articles.decrease')}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">{t('articles.quantity')}</label>
                      <Input
                        type="number"
                        min="1"
                        value={adjustmentQty || ''}
                        onChange={(e) => setAdjustmentQty(parseInt(e.target.value) || 0)}
                        className="h-10"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t('articles.reason')}</label>
                    <Textarea
                      value={adjustmentReason}
                      onChange={(e) => setAdjustmentReason(e.target.value)}
                      placeholder={t('articles.reasonPlaceholder')}
                      className="min-h-[60px] resize-none"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={handleStockAdjustment}
                    disabled={updateStock.isPending || adjustmentQty <= 0}
                    className="w-full h-10"
                    variant={adjustmentType === 'decrease' ? 'destructive' : 'default'}
                  >
                    {updateStock.isPending && <Spinner size="sm" className="mr-2" />}
                    {t('articles.applyAdjustment')}
                    {adjustmentQty > 0 && (
                      <span className="ml-1">
                        ({adjustmentType === 'increase' ? '+' : '-'}{adjustmentQty})
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:flex-1"
              onClick={() => navigate(-1)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:flex-1"
              disabled={isLoading}
              onClick={() => {
                printAfterSave.current = true
                void form.handleSubmit(onSubmit)()
              }}
            >
              <Printer className="h-4 w-4 mr-2" />
              {t('labels.saveAndPrint')}
            </Button>
            <Button
              type="submit"
              className="h-12 sm:flex-1"
              disabled={isLoading}
              onClick={() => {
                printAfterSave.current = false
              }}
            >
              {isLoading && <Spinner size="sm" className="mr-2" />}
              {isEditing ? t('common.update') : t('articles.createArticle')}
            </Button>
          </div>
        </form>
      </Form>

      <AlertDialog
        open={!!sharedCodeConfirm}
        onOpenChange={(open) => !open && setSharedCodeConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('articles.sharedCodeTitle', { code: sharedCodeConfirm?.code ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <ul className="space-y-1 max-h-48 overflow-auto">
                  {sharedCodeConfirm?.articles.map((item) => (
                    <li key={item.id} className="text-foreground">
                      <span className="font-medium">{item.code}</span> · {item.description}
                    </li>
                  ))}
                </ul>
                <p>{t('articles.sharedCodeQuestion')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12 sm:h-10">{t('articles.sharedCodeCancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="h-12 sm:h-10"
              onClick={() => {
                const pending = sharedCodeConfirm
                setSharedCodeConfirm(null)
                if (pending) void saveArticle(pending.values, true)
              }}
            >
              {t('articles.sharedCodeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CameraScannerDialog open={showCamera} onOpenChange={setShowCamera} onCode={handleCameraCode} />
    </div>
  )
}
